import { useForm, zodResolver } from '@mantine/form';
import { mergeWith } from 'lodash';

import { Transit } from '@e-origin/shared';

import { useUpdateTransit } from '@hooks/transit';
import { TransitViewGeneralInfoFormValues, transitViewGeneralInfoSchema } from './validateTransitViewGeneralInfoForm';

export const useTransitViewGeneralInfoFormInitializer = ({ transit, openOverwriteMessage }) => {
  const updateTransitMutation = useUpdateTransit();

  const buildFormValues = (t) => ({
    customer: t.customer ?? '',
    TransitOperation: {
      security: t.TransitOperation?.security ?? '',
      additionalDeclarationType: t.TransitOperation?.additionalDeclarationType ?? '',
      specificCircumstanceIndicator: t.TransitOperation?.specificCircumstanceIndicator ?? '',
      bindingItinerary: t.TransitOperation?.bindingItinerary ?? '',
      limitDate: t.TransitOperation?.limitDate ?? '',
    },
    CustomsOfficeOfDeparture: {
      referenceNumber: t.CustomsOfficeOfDeparture?.referenceNumber ?? '',
    },
    CustomsOfficeOfDestinationDeclared: {
      referenceNumber: t.CustomsOfficeOfDestinationDeclared?.referenceNumber ?? '',
    },
    CustomsOfficeOfDestinationActual: {
      referenceNumber: t.CustomsOfficeOfDestinationActual?.referenceNumber ?? '',
    },
    Guarantee: (t.Guarantee ?? []).map((guarantee) => ({
      guaranteeType: guarantee.guaranteeType ?? '',
      GuaranteeReference: (guarantee.GuaranteeReference ?? []).map((guaranteeReference) => ({
        accessCode: guaranteeReference.accessCode ?? '',
        currency: guaranteeReference.currency ?? '',
        GRN: guaranteeReference.GRN ?? '',
        amountToBeCovered: guaranteeReference.amountToBeCovered ?? '',
        setFromCustomer: guaranteeReference.setFromCustomer ?? false,
      })),
    })),
    Authorisation: (t.Authorisation ?? []).map((authorisation) => ({
      type: authorisation.type ?? '',
      referenceNumber: authorisation.referenceNumber ?? '',
    })),
    CustomsOfficeOfTransitDeclared: (t.CustomsOfficeOfTransitDeclared ?? []).map((customsOfficeOfTransitDeclared) => ({
      referenceNumber: customsOfficeOfTransitDeclared.referenceNumber ?? '',
      arrivalDateAndTimeEstimated: customsOfficeOfTransitDeclared.arrivalDateAndTimeEstimated ?? '',
    })),
    Consignment: {
      grossMass: t.Consignment?.grossMass ?? '',
      referenceNumberUCR: t.Consignment?.referenceNumberUCR ?? '',
      countryOfDestination: t.Consignment?.countryOfDestination ?? '',
      countryOfDispatch: t.Consignment?.countryOfDispatch ?? '',
      inlandModeOfTransport: t.Consignment?.inlandModeOfTransport ?? '',
      modeOfTransportAtTheBorder: t.Consignment?.modeOfTransportAtTheBorder ?? '',
      PlaceOfLoading: {
        UNLocode: t.Consignment?.PlaceOfLoading?.UNLocode ?? '',
        country: t.Consignment?.PlaceOfLoading?.country ?? '',
        location: t.Consignment?.PlaceOfLoading?.location ?? '',
      },
      PlaceOfUnloading: {
        UNLocode: t.Consignment?.PlaceOfUnloading?.UNLocode ?? '',
        country: t.Consignment?.PlaceOfUnloading?.country ?? '',
        location: t.Consignment?.PlaceOfUnloading?.location ?? '',
      },
      LocationOfGoods: {
        UNLocode: t.Consignment?.LocationOfGoods?.UNLocode ?? '',
        typeOfLocation: t.Consignment?.LocationOfGoods?.typeOfLocation ?? '',
        qualifierOfIdentification: t.Consignment?.LocationOfGoods?.qualifierOfIdentification ?? '',
        additionalIdentifier: t.Consignment?.LocationOfGoods?.additionalIdentifier ?? '',
        Address: {
          streetAndNumber: t.Consignment?.LocationOfGoods?.Address?.streetAndNumber ?? '',
          postcode: t.Consignment?.LocationOfGoods?.Address?.postcode ?? '',
          city: t.Consignment?.LocationOfGoods?.Address?.city ?? '',
          country: t.Consignment?.LocationOfGoods?.Address?.country ?? '',
        },
        PostcodeAddress: {
          postcode: t.Consignment?.LocationOfGoods?.PostcodeAddress?.postcode ?? '',
          country: t.Consignment?.LocationOfGoods?.PostcodeAddress?.country ?? '',
          houseNumber: t.Consignment?.LocationOfGoods?.PostcodeAddress?.houseNumber ?? '',
        },
      },
      TransportEquipment: (t.Consignment?.TransportEquipment ?? []).map((transportEquipment) => ({
        containerIdentificationNumber: transportEquipment.containerIdentificationNumber ?? '',
        Seal: (transportEquipment.Seal ?? []).map((seal) => ({
          identifier: seal.identifier ?? '',
        })),
      })),
      DepartureTransportMeans: (t.Consignment?.DepartureTransportMeans ?? []).map((departureTransportMeans) => ({
        typeOfIdentification: departureTransportMeans.typeOfIdentification ?? '',
        identificationNumber: departureTransportMeans.identificationNumber ?? '',
        nationality: departureTransportMeans.nationality ?? '',
      })),
      CountryOfRoutingOfConsignment: (t.Consignment?.CountryOfRoutingOfConsignment ?? []).map((countryOfRoutingOfConsignment) => ({
        country: countryOfRoutingOfConsignment.country ?? '',
      })),
      ActiveBorderTransportMeans: (t.Consignment?.ActiveBorderTransportMeans ?? []).map((activeBorderTransportMeans) => ({
        typeOfIdentification: activeBorderTransportMeans.typeOfIdentification ?? '',
        identificationNumber: activeBorderTransportMeans.identificationNumber ?? '',
        nationality: activeBorderTransportMeans.nationality ?? '',
        customsOfficeAtBorderReferenceNumber: activeBorderTransportMeans.customsOfficeAtBorderReferenceNumber ?? '',
        conveyanceReferenceNumber: activeBorderTransportMeans.conveyanceReferenceNumber ?? '',
      })),
      TransportCharges: {
        methodOfPayment: t.Consignment?.TransportCharges?.methodOfPayment ?? '',
      },
      HouseConsignment: (t.Consignment?.HouseConsignment ?? []).map((houseConsignment) => ({
        ConsignmentItem: (houseConsignment.ConsignmentItem ?? []).map((consignmentItem) => ({
          Commodity: {
            CommodityCode: {
              initialAmountToPayForDuties: consignmentItem.Commodity?.CommodityCode?.initialAmountToPayForDuties ?? '',
              initialAmountToPayForVat: consignmentItem.Commodity?.CommodityCode?.initialAmountToPayForVat ?? '',
            },
          },
        })),
      })),
    },
    refreshCustomerData: false,
  });

  const form = useForm<TransitViewGeneralInfoFormValues>({
    initialValues: buildFormValues(transit),
    validate: zodResolver(transitViewGeneralInfoSchema),
    validateInputOnBlur: true,
  });

  const isDataOverwritten = (data: Partial<Transit>) => {
    if (
      transit.Consignment?.HouseConsignment?.some((house) => {
        return (
          (data.Consignment?.countryOfDispatch && house.countryOfDispatch) ||
          (data.Consignment?.countryOfDestination && house.countryOfDestination) ||
          (data.Consignment?.referenceNumberUCR && house.referenceNumberUCR) ||
          house.ConsignmentItem?.some(
            (item) =>
              (data.TransitOperation?.declarationType && item.declarationType) ||
              ((data.Consignment?.countryOfDispatch || house.countryOfDispatch) && item.countryOfDispatch) ||
              ((data.Consignment?.countryOfDestination || house.countryOfDestination) && item.countryOfDestination) ||
              ((data.Consignment?.referenceNumberUCR || house.referenceNumberUCR) && item.referenceNumberUCR),
          )
        );
      })
    ) {
      return true;
    }

    return false;
  };

  const onSubmit = async (values, isOverwriteConfirm) => {
    if (!isOverwriteConfirm && isDataOverwritten(values)) {
      openOverwriteMessage();
      return;
    }

    const { refreshCustomerData } = values;

    delete values.refreshCustomerData;

    updateTransitMutation.mutate({
      transit: mergeWith({ _id: transit._id }, values, (objValue, srcValue) => (Array.isArray(srcValue) ? srcValue : undefined)),
      refreshCustomerData,
    });
  };

  return { form, onSubmit, buildFormValues };
};
